/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import { styled } from "@linaria/react";
import classNames from "classnames";
import type { FilterOption } from "./FilterSelect";
import FilterSelect from "./FilterSelect";
import t from "../i18n";
import Reset from "./Reset";
import { isFuncState } from "../lib/isFuncState";

export const FilterWrapper = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  row-gap: 8px;
  &.wrappable {
    flex-wrap: wrap;
  }
`;

export const NONE_LABEL = t("filter.none");

function uniqBy<T>(array: T[], getter: ((item: T) => string) | keyof T) {
  const cb = typeof getter === "function" ? getter : (o: T) => o[getter];

  return Array.from(
    array
      .reduce((map, item) => {
        const key = item === null || item === undefined ? item : cb(item);

        if (!map.has(key)) map.set(key, item);
        return map;
      }, new Map<unknown, T>())
      .values(),
  );
}

export function buildOptions<T extends string | null>(
  options: FilterOption<T>[],
  sortBy?: (d: FilterOption<T>) => string,
) {
  return uniqBy(options, "value").sort((a, b) => {
    if (a.value !== null && b.value === null) return 1;
    if (a.value === null && b.value !== null) return -1;
    if (sortBy) return sortBy(a).localeCompare(sortBy(b));
    return a.label.localeCompare(b.label);
  });
}

export function buildOptionsFrom<T, U extends string | null>(
  items: T[],
  mapping: { value: (item: T) => U; label: (item: T) => string; description?: (item: T) => string },
): FilterOption<U>[] {
  return buildOptions(
    items.map(item => ({
      value: mapping.value(item),
      label: mapping.label(item),
      description: mapping.description?.(item),
    })),
  );
}

export interface FilterMetadata<Data, ValueType extends string | null> {
  title: string;
  getOptions: (d: Data) => FilterOption<ValueType> | FilterOption<ValueType>[] | null;
  hidden?: boolean;
  filtered?: boolean;
  sortBy?: (d: FilterOption<ValueType>) => string;
  withNone?: boolean;
  showTag?: boolean;
  tagLabel?: string;
}

type GetOptionsReturnType<M extends FilterMetadata<any, string | null>> = Exclude<
  ReturnType<M["getOptions"]>,
  null
>;
type MetadataOptionValueType<M extends FilterMetadata<any, string | null>> =
  GetOptionsReturnType<M> extends { value: any }
    ? GetOptionsReturnType<M>["value"]
    : GetOptionsReturnType<M> extends { value: any }[]
      ? GetOptionsReturnType<M>[0]["value"]
      : never;

export interface FilterSelectsProps<
  Data,
  FilterKey extends string,
  Metadata extends { [K in FilterKey]: FilterMetadata<Data, string | null> },
> extends React.HTMLAttributes<HTMLDivElement> {
  data: Data[];
  metadata: Metadata;

  filterValues: { [K in FilterKey]: MetadataOptionValueType<Metadata[K]>[] };
  setFilterValues: (
    filterValues: React.SetStateAction<{
      [K in FilterKey]: MetadataOptionValueType<Metadata[K]>[];
    }>,
  ) => void;
  defaultFilterValues: { [K in FilterKey]: MetadataOptionValueType<Metadata[K]>[] };

  // allows to filter filterValues through input value
  wrappable?: boolean;
  withReset?: boolean;
  tagLabel?: string;
  hidden?: boolean;
}

const FilterSelects = <
  Data,
  FilterKey extends string,
  Metadata extends { [K in FilterKey]: FilterMetadata<Data, string | null> },
>({
  data,
  metadata,
  filterValues,
  setFilterValues,
  defaultFilterValues,
  className,
  wrappable = false,
  withReset = true,
  tagLabel,
}: FilterSelectsProps<Data, FilterKey, Metadata>) => {
  const filterKeys = React.useMemo(
    () => (Object.keys(metadata) as FilterKey[]).filter(c => !metadata[c].hidden),
    [metadata],
  );

  const optionByFilterKey = React.useMemo(
    () =>
      filterKeys.reduce(
        (acc, filterKey) => {
          const allOptions = data.reduce<FilterOption<string | null>[]>((acc2, item) => {
            const filterOption = metadata[filterKey].getOptions(item);
            const filterOptions = Array.isArray(filterOption) ? filterOption : [filterOption];
            const withNone = metadata[filterKey].withNone;

            if ((filterOption === null || filterOptions.length < 1) && withNone)
              acc2.push({ label: NONE_LABEL, value: null });

            for (const option of filterOptions) {
              if (option && (option.value !== null || withNone)) {
                acc2.push(option);
              }
            }

            return acc2;
          }, []);

          acc[filterKey] = buildOptions(allOptions, metadata[filterKey].sortBy);

          return acc;
        },
        {} as { [k in FilterKey]: FilterOption<string | null>[] },
      ),
    [data, filterKeys, metadata],
  );
  return (
    <FilterWrapper className={classNames(className, { wrappable })}>
      {filterKeys.map(filterKey => (
        <FilterSelect<string | null>
          key={filterKey}
          title={metadata[filterKey].title}
          options={optionByFilterKey[filterKey]}
          filtered={metadata[filterKey].filtered}
          values={filterValues[filterKey]}
          setValues={function (values: (string | null)[]): void {
            setFilterValues(
              (state: { [K in FilterKey]: MetadataOptionValueType<Metadata[K]>[] }) => {
                const newState = { ...state };
                newState[filterKey] = values as MetadataOptionValueType<Metadata[FilterKey]>[];
                return newState;
              },
            );
          }}
          tagLabel={tagLabel}
          showTag={metadata[filterKey].showTag}
          hidden={metadata[filterKey].hidden}
        />
      ))}
      {withReset && (
        <Reset
          filterValues={filterValues}
          setFilterValues={setFilterValues}
          defaultFilterValues={defaultFilterValues}
        />
      )}
    </FilterWrapper>
  );
};

export default FilterSelects;

export function filterData<
  Data,
  FilterKey extends string,
  Metadata extends { [K in FilterKey]: FilterMetadata<Data, string | null> },
>(metadata: Metadata) {
  const filterKeys = Object.keys(metadata) as FilterKey[];

  return (
    data: Data[],
    filteredValues: { [K in FilterKey]: MetadataOptionValueType<Metadata[K]>[] },
  ) =>
    data.filter(item =>
      filterKeys.every(filterKey => {
        const selectedValues = filteredValues[filterKey];
        const option = metadata[filterKey].getOptions(item);
        const options = Array.isArray(option) ? option : [option];

        return (
          selectedValues.length === 0 ||
          options.some(
            option =>
              option &&
              selectedValues.includes(option.value as MetadataOptionValueType<Metadata[FilterKey]>),
          )
        );
      }),
    );
}

export function useSubStateSetter<T, K extends keyof T>(
  setter: (state: React.SetStateAction<T>) => void,
  key: K,
  hook?: () => void,
) {
  return React.useCallback(
    (newValue: React.SetStateAction<T[K]>) => {
      hook?.();
      setter(oldState => ({
        ...oldState,
        [key]: isFuncState(newValue) ? newValue(oldState[key]) : newValue,
      }));
    },
    [setter, key, hook],
  );
}
